import { Formik } from 'formik';
import { type ComponentType, type Dispatch, type PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentStatus } from '../../../../../api/types';
import { useThemeComponents } from '../../../../../themes';
import { type DefaultLayoutProps } from '../../../../../themes/default/Standard/Layout';
import useHandleError from '../../../../../utilities/useHandleError';
import useValidator from '../../../../../utilities/useValidator';
import useAgreementsValidator from '../../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValidator';
import useAgreementsValues, {
    type AgreementValues,
} from '../../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValues';
import useGiftVoucherPaymentAgreements from '../AdyenPaymentPage/useGiftVoucherPaymentAgreements';
import { type GiftVoucherAction, type GiftVoucherState } from '../reducer';
import Inner from './Inner';
import useGiftVoucherFiservDepositSubmission, { submitPayment } from './useFiservDepositSubmission';

export type GiftVoucherFiservPaymentPageProps = {
    state: GiftVoucherState;
    dispatch: Dispatch<GiftVoucherAction>;
    CustomLayout: ComponentType<PropsWithChildren<DefaultLayoutProps>>;
    promoCodeModuleId?: string;
    applicationModuleId?: string;
};

type FiservPaymentFormValues = AgreementValues & { promoCodeId?: string };

const GiftVoucherFiservPaymentPage = ({
    state,
    dispatch,
    CustomLayout,
    ...props
}: GiftVoucherFiservPaymentPageProps) => {
    const { token, giftVoucher } = state;
    const { t } = useTranslation(['paymentDetails']);
    const { notification } = useThemeComponents();

    const paymentAgreements = useGiftVoucherPaymentAgreements(state);
    const initialValues = useAgreementsValues(paymentAgreements);
    const validator = useAgreementsValidator(paymentAgreements);
    const validation = useValidator(validator);

    const submitFiservDeposit = useGiftVoucherFiservDepositSubmission();

    useEffect(() => {
        if ([PaymentStatus.Failed, PaymentStatus.Error].includes(giftVoucher.deposit.status)) {
            notification.error(t('paymentDetails:resultCode.error'));
        }
    }, [giftVoucher.deposit.status, notification, t]);

    const onSubmit = useHandleError(
        async (values: FiservPaymentFormValues) => {
            notification.loading({
                content: t('paymentDetails:messages.paymentRedirecting'),
                duration: 0,
                key: 'primary',
            });
            const { promoCodeId, ...agreementValues } = values;

            if (!giftVoucher.deposit.completedAt && giftVoucher.deposit.amount !== 0) {
                try {
                    // call payment submission api
                    const paymentResult = await submitPayment(agreementValues, token, promoCodeId);
                    const { redirectUrl } = paymentResult;

                    if (redirectUrl) {
                        // redirect to 3DS
                        window.location.replace(redirectUrl);

                        return;
                    }

                    // there's an error
                    notification.error(t('paymentDetails:resultCode.error'));
                } catch {
                    notification.error(t('paymentDetails:resultCode.error'));
                }
            }
        },
        [notification, t, giftVoucher, submitFiservDeposit, token, dispatch],
        {}
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={state.giftVoucher.deposit.amount > 0 ? validation : null}
        >
            <Inner CustomLayout={CustomLayout} dispatch={dispatch} state={state} {...props} />
        </Formik>
    );
};

export default GiftVoucherFiservPaymentPage;
